/* Rangnoraa Living Wardrobe 3.2.12 — full catalog mannequins and connected carousel. */
const instances = new WeakMap();
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];

function parseJSON(script, fallback = []) {
  if (!script) return fallback;
  try { return JSON.parse(script.textContent) ?? fallback; } catch (_) { return fallback; }
}
function displayTitle(value) {
  return String(value || '').replace(/[_-]+/g, ' ').toLowerCase().replace(/\b[a-z]/g, letter => letter.toUpperCase());
}
function stripHTML(value) {
  const box = document.createElement('div');
  box.innerHTML = String(value || '');
  return (box.textContent || '').replace(/\s+/g, ' ').trim();
}

/* Feet and full-body heights are measured from the opening image's actual statues. */
function showroomLayout(width, height) {
  const mobile = width < 761 || height > width;
  const imageWidth = mobile ? 941 : 1672;
  const imageHeight = mobile ? 1672 : 941;
  const cover = Math.max(width / imageWidth, height / imageHeight);
  const cropX = (width - imageWidth * cover) / 2;
  const cropY = (height - imageHeight * cover) / 2;
  const stops = mobile
    ? [[.158, .551, .213], [.545, .632, .416], [.817, .573, .213]]
    : [[.306, .662, .304], [.501, .765, .548], [.702, .679, .302]];
  return stops.map(([x, y, size]) => ({
    x: cropX + x * imageWidth * cover,
    y: cropY + y * imageHeight * cover,
    height: size * imageHeight * cover
  }));
}

function initWardrobe(root) {
  if (instances.has(root)) return;

  const viewport = $('[data-wardrobe-viewport]', root);
  const canvasHost = $('[data-wardrobe-canvas]', root);
  const featuredScript = $('[data-wardrobe-products]', root);
  const orderedScript = $('[data-wardrobe-ordered-products]', root);
  const catalogScript = $('[data-wardrobe-catalog-products]', root);
  const categoryScript = $('[data-wardrobe-category-products]', root);
  if (!viewport || !canvasHost || !featuredScript) return;
  const focusPreview = $('[data-wardrobe-focus-preview]', viewport);
  const focusImage = $('[data-wardrobe-focus-image]', focusPreview);
  const fallbackLooks = $$('[data-wardrobe-fallback-slot]', viewport);

  const featured = parseJSON(featuredScript).filter(Boolean);
  const ordered = parseJSON(orderedScript).filter(Boolean);
  const catalogSeed = parseJSON(catalogScript).filter(Boolean);
  const categorySeed = parseJSON(categoryScript).filter(entry => entry?.category && entry?.product?.handle);
  const itemByHandle = new Map();
  const catalogItems = [];
  const categoryProducts = new Map();

  const addItem = item => {
    if (!item?.handle) return;
    const existing = itemByHandle.get(item.handle);
    if (existing) {
      Object.keys(item).forEach(key => {
        if ((existing[key] === null || existing[key] === undefined || existing[key] === '') && item[key] !== null && item[key] !== undefined) existing[key] = item[key];
      });
      return existing;
    }
    item._hydrated = Array.isArray(item.variants) && item.variants.length > 0;
    itemByHandle.set(item.handle, item);
    catalogItems.push(item);
    return item;
  };
  /* Product order blocks define the carousel sequence; older settings keep the opening trio. */
  if (ordered.length) {
    ordered.forEach(addItem);
    featured.forEach(addItem);
  } else if (featured.length >= 3) [featured[1], featured[0], featured[2]].forEach(addItem);
  else featured.forEach(addItem);
  catalogSeed.forEach(addItem);
  categorySeed.forEach(({category, product}) => {
    if (!categoryProducts.has(category)) categoryProducts.set(category, new Set());
    categoryProducts.get(category).add(product.handle);
    addItem(product);
  });

  const shop = $('[data-wardrobe-shop]', viewport);
  const quickPrice = $('[data-wardrobe-quick-price]', shop);
  const quickOptions = $('[data-wardrobe-quick-options]', shop);
  const quickVariant = $('[data-wardrobe-quick-variant]', shop);
  const quickQuantity = $('[data-wardrobe-quick-quantity]', shop);
  const quickBuy = $('[data-wardrobe-buy-now]', shop);
  const quickStatus = $('[data-wardrobe-quick-status]', shop);
  const productDetailsLink = $('[data-wardrobe-product-details]', shop);
  const focusDetailsLink = $('[data-wardrobe-focus-details]', focusPreview);

  const inspector = $('[data-wardrobe-inspector]', viewport);
  const catalogPanel = $('[data-wardrobe-catalog]', viewport);
  const catalogGrid = $('[data-wardrobe-catalog-grid]', catalogPanel);
  const categoryTrack = $('[data-wardrobe-category-track]', catalogPanel);
  const categoryButtons = $$('[data-wardrobe-category]', categoryTrack || catalogPanel);
  const categoryLink = $('[data-wardrobe-category-link]', catalogPanel);
  const categoryEmpty = $('[data-wardrobe-category-empty]', catalogPanel);
  const catalogCards = new Map();
  $$('[data-wardrobe-catalog-product]', catalogGrid).forEach(card => {
    const handle = card.dataset.wardrobeCatalogProduct;
    if (catalogCards.has(handle)) card.remove();
    else catalogCards.set(handle, card);
  });
  catalogItems.forEach(item => {
    const card = catalogCards.get(item.handle);
    if (card) catalogGrid.append(card);
  });
  function selectCategory(handle = 'all') {
    const selected = categoryButtons.find(button => button.dataset.wardrobeCategory === handle);
    if (handle !== 'all' && !selected) return;
    categoryButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.wardrobeCategory === handle)));
    let shown = 0;
    catalogCards.forEach((card, productHandle) => {
      card.hidden = handle !== 'all' && !categoryProducts.get(handle)?.has(productHandle);
      if (!card.hidden) shown++;
    });
    if (categoryEmpty) categoryEmpty.hidden = shown > 0;
    if (categoryLink) {
      categoryLink.href = selected?.dataset.categoryUrl || categoryLink.dataset.allUrl || '/collections/all';
      categoryLink.textContent = handle === 'all' ? 'Open the complete collection page ↗' : `Open ${selected.textContent.trim()} collection ↗`;
    }
    selected?.scrollIntoView?.({block: 'nearest', inline: 'nearest', behavior: 'smooth'});
  }
  categoryButtons.forEach(button => button.addEventListener('click', () => selectCategory(button.dataset.wardrobeCategory)));
  $('[data-wardrobe-category-prev]', catalogPanel)?.addEventListener('click', () => categoryTrack?.scrollBy({left: -180, behavior: 'smooth'}));
  $('[data-wardrobe-category-next]', catalogPanel)?.addEventListener('click', () => categoryTrack?.scrollBy({left: 180, behavior: 'smooth'}));
  const inspectorTitle = $('[data-wardrobe-title]', inspector);
  const inspectorPrice = $('[data-wardrobe-price]', inspector);
  const inspectorPhoto = $('[data-wardrobe-photo]', inspector);
  const inspectorPhotoCount = $('[data-wardrobe-photo-count]', inspector);
  const inspectorDescription = $('[data-wardrobe-description]', inspector);
  const actualLink = $('[data-wardrobe-actual]', inspector);

  const prevButton = $('[data-wardrobe-prev]', viewport);
  const nextButton = $('[data-wardrobe-next]', viewport);
  const groupCounter = $('[data-wardrobe-counter]', viewport);
  const captionEl = $('[data-wardrobe-caption]', viewport);

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.motion === 'off';
  const localeRoot = ((window.Shopify && Shopify.routes && Shopify.routes.root) || window.Rangnoraa?.root || '/').replace(/\/?$/, '/');
  const currency = window.Rangnoraa?.currency || 'INR';
  const money = amount => new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
    style: 'currency', currency, maximumFractionDigits: 2
  }).format((Number(amount) || 0) / 100);

  const hydration = new Map();
  let sceneController = null;
  let disposed = false;
  let centerIndex = ordered.length ? 0 : (catalogItems.length > 1 ? 1 : 0);
  let selectedSlot = 1;
  let focused = false;
  let photoIndex = 0;
  let renderToken = 0;
  let stageStep = -1;
  let gestureStartY = null;
  let gestureStartX = null;
  let carouselLockUntil = 0;
  let lastJourneyProgress = null;
  let pinchStartDistance = null;
  let pinchStartZoom = 1;
  let pinchZoom = 1;
  let wasPinching = false;
  let suppressLookClickUntil = 0;

  function wrapIndex(index) {
    const length = catalogItems.length;
    if (!length) return -1;
    return ((index % length) + length) % length;
  }
  function trioAtCenter(index = centerIndex) {
    if (!catalogItems.length) return [null, null, null];
    if (catalogItems.length === 1) return [null, catalogItems[0], null];
    if (catalogItems.length === 2) return [catalogItems[wrapIndex(index - 1)], catalogItems[wrapIndex(index)], null];
    return [
      catalogItems[wrapIndex(index - 1)],
      catalogItems[wrapIndex(index)],
      catalogItems[wrapIndex(index + 1)]
    ];
  }
  function activeGroup() { return trioAtCenter(centerIndex); }
  function activeProduct() { return activeGroup()[1] || activeGroup().find(Boolean) || null; }
  function centerProductName() {
    const product = activeProduct();
    return product ? displayTitle(product.title) : 'Explore the collection.';
  }
  function updateFallbackLooks() {
    const layout = showroomLayout(viewport.clientWidth || innerWidth, viewport.clientHeight || innerHeight);
    const group = activeGroup();
    fallbackLooks.forEach((button, index) => {
      const item = group[index];
      const source = item?.cutout || item?.image;
      button.hidden = !source;
      if (!source) return;
      const img = $('img', button);
      if (img.getAttribute('src') !== source) img.src = source;
      img.alt = displayTitle(item.title);
      button.setAttribute('aria-label', `View ${displayTitle(item.title)}`);
      button.classList.toggle('has-cutout', Boolean(item.cutout));
      button.style.left = `${layout[index].x}px`;
      button.style.top = `${layout[index].y}px`;
      button.style.height = `${layout[index].height}px`;
      button.style.zIndex = index === 1 ? '2' : '1';
    });
  }
  function animateFallbackSlide(previousGroup, direction, outgoingGhost) {
    if (reduced || viewport.classList.contains('is-3d-ready')) return;
    const layout = showroomLayout(viewport.clientWidth || innerWidth, viewport.clientHeight || innerHeight);
    const nextGroup = activeGroup();
    const width = viewport.clientWidth || innerWidth;
    const duration = 620;
    const frame = point => ({left: `${point.x}px`, top: `${point.y}px`, height: `${point.height}px`});
    const outgoing = direction > 0 ? 0 : 2;
    const outgoingButton = fallbackLooks[outgoing];
    if (previousGroup[outgoing]?.handle && outgoingButton && outgoingGhost) {
      const ghost = outgoingGhost;
      ghost.removeAttribute('data-wardrobe-fallback-slot');
      ghost.setAttribute('aria-hidden', 'true');
      ghost.tabIndex = -1;
      ghost.style.left = `${layout[outgoing].x}px`;
      ghost.style.top = `${layout[outgoing].y}px`;
      ghost.style.height = `${layout[outgoing].height}px`;
      ghost.style.pointerEvents = 'none';
      outgoingButton.parentElement.append(ghost);
      const ghostAnimation = ghost.animate?.([
        {...frame(layout[outgoing]), opacity: 1},
        {...frame({...layout[outgoing], x: layout[outgoing].x + (direction > 0 ? -width * .58 : width * .58)}), opacity: 0}
      ], {duration: duration * .52, easing: 'ease-in', fill: 'forwards'});
      if (ghostAnimation) ghostAnimation.finished.then(() => ghost.remove(), () => ghost.remove());
      else ghost.remove();
    }
    fallbackLooks.forEach((button, index) => {
      const item = nextGroup[index];
      if (!item || button.hidden) return;
      const oldIndex = previousGroup.findIndex(old => old?.handle === item.handle);
      const start = oldIndex < 0
        ? {...layout[index], x: layout[index].x + (direction > 0 ? width * .58 : -width * .58)}
        : layout[oldIndex];
      button.getAnimations?.().forEach(animation => animation.cancel());
      button.animate?.([
        {...frame(start), opacity: oldIndex < 0 ? 0 : 1},
        {...frame(layout[index]), opacity: 1}
      ], {duration, easing: 'cubic-bezier(.33,.02,.2,1)', fill: 'backwards'});
    });
  }
  function preloadAround(index) {
    if (!catalogItems.length) return;
    for (let offset = -2; offset <= 2; offset++) {
      const item = catalogItems[wrapIndex(index + offset)];
      const source = item?.cutout || item?.image;
      if (!source) continue;
      const image = new Image();
      image.decoding = 'async';
      image.src = source;
    }
  }

  function normalizeRemoteProduct(base, payload) {
    const variants = (payload.variants || []).map(variant => {
      const options = Array.isArray(variant.options)
        ? variant.options
        : [variant.option1, variant.option2, variant.option3].filter(value => value !== null && value !== undefined);
      return {...variant, id: Number(variant.id), price: Number(variant.price || 0), available: Boolean(variant.available), options};
    });
    const optionNames = (payload.options || []).map(option => typeof option === 'string' ? option : option?.name).filter(Boolean);
    const options = optionNames.map((name, index) => ({
      name,
      values: [...new Set(variants.map(variant => variant.options[index]).filter(value => value !== undefined && value !== null && value !== ''))]
    }));
    Object.assign(base, {
      id: payload.id ?? base.id,
      handle: payload.handle || base.handle,
      title: payload.title || base.title,
      url: localeRoot + 'products/' + encodeURIComponent(payload.handle || base.handle),
      available: payload.available ?? variants.some(variant => variant.available),
      price: Number(payload.price ?? base.price ?? variants[0]?.price ?? 0),
      description: stripHTML(payload.description || base.description || ''),
      image: payload.featured_image || base.image || payload.images?.[0] || null,
      images: Array.isArray(payload.images) ? payload.images : (base.images || []),
      variants,
      options,
      _hydrated: true
    });
    return base;
  }

  async function hydrateProduct(item) {
    if (!item?.handle) return item;
    if (item._hydrated && Array.isArray(item.variants) && item.variants.length) return item;
    if (hydration.has(item.handle)) return hydration.get(item.handle);
    const request = fetch(localeRoot + 'products/' + encodeURIComponent(item.handle) + '.js', {
      headers: {Accept: 'application/json'}
    }).then(response => {
      if (!response.ok) throw new Error('Could not load sizes and colours.');
      return response.json();
    }).then(payload => normalizeRemoteProduct(item, payload)).finally(() => hydration.delete(item.handle));
    hydration.set(item.handle, request);
    return request;
  }

  function preferredVariant(item) {
    if (!item?.variants?.length) return null;
    return item.variants.find(variant => variant.available) || item.variants[0];
  }

  function currentQuickVariant(item = activeProduct()) {
    if (!item?.variants?.length) return null;
    const selectedValues = $$('select[data-wardrobe-option-index]', quickOptions)
      .sort((a, b) => Number(a.dataset.wardrobeOptionIndex) - Number(b.dataset.wardrobeOptionIndex))
      .map(select => select.value);
    if (!selectedValues.length) return preferredVariant(item);
    return item.variants.find(variant => selectedValues.every((value, index) => String(variant.options[index]) === String(value))) || null;
  }

  function renderQuickVariant(item = activeProduct()) {
    const variant = currentQuickVariant(item);
    if (variant) quickPrice.textContent = money(variant.price);
    quickVariant.value = variant?.id || '';
    const available = Boolean(variant?.available);
    quickBuy.disabled = !available;
    quickBuy.firstChild.textContent = available ? 'Buy now ' : 'Unavailable ';
    quickStatus.textContent = available ? '' : (item ? 'This combination is currently unavailable.' : 'No product selected.');
  }

  function buildQuickOptions(item) {
    quickOptions.replaceChildren();
    const first = preferredVariant(item);
    (item?.options || []).forEach((option, optionIndex) => {
      if (!option?.values?.length) return;
      if (String(option.name).toLowerCase() === 'title' && option.values.length === 1) return;
      const field = document.createElement('label');
      field.className = 'wardrobe-quick-field';
      const label = document.createElement('span');
      const optionName = /colou?r/i.test(option.name) ? 'COLOUR' : String(option.name || 'OPTION').toUpperCase();
      label.textContent = optionName;
      const select = document.createElement('select');
      select.dataset.wardrobeOptionIndex = String(optionIndex);
      select.setAttribute('aria-label', optionName);
      option.values.forEach(value => {
        const optionEl = document.createElement('option');
        optionEl.value = value;
        optionEl.textContent = value;
        select.append(optionEl);
      });
      if (first?.options?.[optionIndex] !== undefined) select.value = first.options[optionIndex];
      select.addEventListener('change', () => renderQuickVariant(item));
      field.append(label, select);
      quickOptions.append(field);
    });
  }

  async function renderQuickProduct(item = activeProduct()) {
    const token = ++renderToken;
    productDetailsLink.href = item?.url || (item?.handle ? localeRoot + 'products/' + encodeURIComponent(item.handle) : localeRoot + 'collections/all');
    focusDetailsLink.href = productDetailsLink.href;
    quickPrice.textContent = item ? money(item.price) : '';
    quickOptions.replaceChildren();
    quickVariant.value = '';
    quickBuy.disabled = true;
    quickStatus.textContent = '';
    if (!item) return;

    if (!item._hydrated || !item.variants?.length) {
      shop.classList.add('is-loading');
      quickStatus.textContent = 'Loading size and colour options…';
      try {
        await hydrateProduct(item);
      } catch (error) {
        if (token !== renderToken) return;
        quickStatus.textContent = 'Open Product More Details to choose this design.';
        shop.classList.remove('is-loading');
        return;
      }
    }
    if (token !== renderToken) return;
    shop.classList.remove('is-loading');
    quickStatus.textContent = '';
    focusDetailsLink.href = item.url || productDetailsLink.href;
    buildQuickOptions(item);
    renderQuickVariant(item);
  }

  function updateGroupUI() {
    const total = Math.max(catalogItems.length, 1);
    const current = catalogItems.length ? centerIndex + 1 : 0;
    groupCounter.textContent = String(current).padStart(2, '0') + ' / ' + String(total).padStart(2, '0');
    prevButton.disabled = catalogItems.length <= 1;
    nextButton.disabled = catalogItems.length <= 1;
    preloadAround(centerIndex);
    updateFallbackLooks();
  }

  function updateFocusUI() {
    viewport.classList.toggle('is-focused', focused);
    focusPreview.setAttribute('aria-hidden', String(!focused));
    if (focused) {
      const item = activeProduct();
      const source = item?.cutout || item?.image || '';
      if (focusImage.getAttribute('src') !== source) {
        if (source) focusImage.src = source;
        else focusImage.removeAttribute('src');
      }
      focusImage.alt = item ? `Complete view of ${displayTitle(item.title)}` : '';
      focusDetailsLink.href = item?.url || (item?.handle ? localeRoot + 'products/' + encodeURIComponent(item.handle) : localeRoot + 'collections/all');
      focusPreview.classList.toggle('has-cutout', Boolean(item?.cutout));
    }
  }

  function resetProductZoom() {
    pinchZoom = 1;
    pinchStartZoom = 1;
    pinchStartDistance = null;
    focusPreview.style.setProperty('--wardrobe-image-zoom', '1');
    focusPreview.style.setProperty('--wardrobe-image-origin', '50% 50%');
    sceneController?.setPinchZoom?.(1);
  }

  function setImageZoomOrigin(clientX, clientY) {
    const bounds = focusImage.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const x = clamp((clientX - bounds.left) / bounds.width * 100, 0, 100);
    const y = clamp((clientY - bounds.top) / bounds.height * 100, 0, 100);
    focusPreview.style.setProperty('--wardrobe-image-origin', `${x}% ${y}%`);
  }

  function setImageZoom(value) {
    pinchZoom = clamp(value, 1, 1.8);
    focusPreview.style.setProperty('--wardrobe-image-zoom', String(pinchZoom));
  }

  function showAllThree() {
    focused = false;
    selectedSlot = 1;
    resetProductZoom();
    updateFocusUI();
    sceneController?.focus(1, false);
    captionEl.textContent = centerProductName();
  }

  function highlightCenter() {
    if (!viewport.classList.contains('has-products') || !activeProduct()) return;
    selectedSlot = 1;
    focused = true;
    updateFocusUI();
    sceneController?.focus(1, true);
    renderQuickProduct(activeProduct());
    captionEl.textContent = centerProductName();
  }

  function moveCarousel(delta) {
    if (!catalogItems.length || catalogItems.length === 1 || Date.now() < carouselLockUntil) return;
    const direction = delta >= 0 ? 1 : -1;
    carouselLockUntil = Date.now() + (reduced ? 140 : 720);
    viewport.classList.add('is-group-changing');
    window.setTimeout(() => viewport.classList.remove('is-group-changing'), reduced ? 160 : 700);

    const previousGroup = activeGroup();
    const outgoingGhost = fallbackLooks[direction > 0 ? 0 : 2]?.cloneNode(true);
    showAllThree();
    centerIndex = wrapIndex(centerIndex + direction);
    updateGroupUI();
    sceneController?.setGroup(activeGroup(), direction, false);
    animateFallbackSlide(previousGroup, direction, outgoingGhost);
    renderQuickProduct(activeProduct());
    captionEl.textContent = centerProductName();
  }

  function handleLookClick(slot) {
    if (Date.now() < suppressLookClickUntil || Date.now() < carouselLockUntil) return;
    if (slot === 1) {
      highlightCenter();
      return;
    }
    moveCarousel(slot === 0 ? -1 : 1);
  }

  function goToCatalogIndex(index, focusItem = true) {
    if (!Number.isInteger(index) || index < 0 || index >= catalogItems.length) return;
    const direction = index === centerIndex ? 1 : (index > centerIndex ? 1 : -1);
    centerIndex = index;
    selectedSlot = 1;
    focused = Boolean(focusItem);
    resetProductZoom();
    updateGroupUI();
    updateFocusUI();
    sceneController?.setGroup(activeGroup(), direction, true);
    sceneController?.focus(1, focused);
    renderQuickProduct(activeProduct());
    captionEl.textContent = centerProductName();
  }

  async function buySelectedProduct() {
    const item = activeProduct();
    if (!item) return;
    if (!item._hydrated) {
      try { await hydrateProduct(item); buildQuickOptions(item); } catch (error) {
        quickStatus.textContent = error.message;
        return;
      }
    }
    const variant = currentQuickVariant(item);
    if (!variant?.available) {
      quickStatus.textContent = 'Please choose an available size and colour.';
      renderQuickVariant(item);
      return;
    }
    const quantity = Math.max(1, Number(quickQuantity.value) || 1);
    quickBuy.disabled = true;
    quickStatus.textContent = 'Preparing checkout…';
    try {
      const formData = new FormData();
      formData.append('id', String(variant.id));
      formData.append('quantity', String(quantity));
      const response = await fetch(localeRoot + 'cart/add.js', {
        method: 'POST',
        headers: {Accept: 'application/json'},
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'We could not add this design.');
      location.assign('/checkout');
    } catch (error) {
      quickStatus.textContent = error.message;
      quickBuy.disabled = !currentQuickVariant(item)?.available;
    }
  }

  function showPhoto(index) {
    const item = activeProduct();
    const photos = item?.images?.length ? item.images : (item?.image ? [item.image] : []);
    photoIndex = photos.length ? (index + photos.length) % photos.length : 0;
    if (photos[photoIndex]) inspectorPhoto.src = photos[photoIndex];
    else inspectorPhoto.removeAttribute('src');
    inspectorPhoto.alt = item ? `${displayTitle(item.title)}, product photo ${photoIndex + 1}` : '';
    inspectorPhotoCount.textContent = String(photoIndex + 1).padStart(2, '0') + ' / ' + String(Math.max(photos.length, 1)).padStart(2, '0');
    $('[data-wardrobe-photo-prev]', inspector).disabled = photos.length < 2;
    $('[data-wardrobe-photo-next]', inspector).disabled = photos.length < 2;
  }

  async function renderInspector() {
    const item = activeProduct();
    if (!item) return;
    inspectorTitle.textContent = displayTitle(item.title);
    inspectorPrice.textContent = money(item.price);
    inspectorDescription.textContent = item.description || 'Explore the product photographs for exact colour, fabric detail and fit.';
    actualLink.href = item.url || (localeRoot + 'products/' + encodeURIComponent(item.handle));
    showPhoto(0);
    try {
      await hydrateProduct(item);
      if (item !== activeProduct()) return;
      inspectorTitle.textContent = displayTitle(item.title);
      inspectorPrice.textContent = money(currentQuickVariant(item)?.price ?? item.price);
      inspectorDescription.textContent = item.description || 'Explore the product photographs for exact colour, fabric detail and fit.';
      actualLink.href = item.url;
      showPhoto(0);
    } catch (_) { /* The catalog link remains usable even if product JSON is unavailable. */ }
  }

  function closePanels() {
    inspector.hidden = true;
    catalogPanel.hidden = true;
    viewport.classList.remove('is-inspecting', 'is-catalog-open');
  }

  function openInspector() {
    catalogPanel.hidden = true;
    viewport.classList.remove('is-catalog-open');
    if (!focused) highlightCenter();
    renderInspector();
    inspector.hidden = false;
    viewport.classList.add('is-inspecting');
    $('[data-wardrobe-inspector-close]', inspector)?.focus({preventScroll: true});
  }

  function openCatalog() {
    inspector.hidden = true;
    viewport.classList.remove('is-inspecting');
    catalogPanel.hidden = false;
    viewport.classList.add('is-catalog-open');
    $('[data-wardrobe-catalog-close]', catalogPanel)?.focus({preventScroll: true});
  }

  function setStage(index) {
    const next = clamp(index, 0, 3);
    if (stageStep === next) return;
    stageStep = next;
    viewport.dataset.step = String(next);
    if (!focused) captionEl.textContent = next === 0 ? 'Swipe up to explore the collection.' : centerProductName();
  }
  function journeyProgress() {
    const roomHeight = root.offsetHeight - window.innerHeight;
    return roomHeight > 0 ? clamp(-root.getBoundingClientRect().top / roomHeight, 0, 1) : 0;
  }
  const enterCue = $('[data-wardrobe-enter-cue]', viewport);
  const productStop = $('[data-wardrobe-product-stop]', root);
  const cueKey = 'rangnoraa-wardrobe-explored-v1';
  let cueDismissed = false;
  try { cueDismissed = window.localStorage?.getItem(cueKey) === '1'; } catch (_) { /* Storage can be unavailable in private browsing. */ }
  document.documentElement.classList.add('wardrobe-snap-enabled');
  function dismissCue() {
    cueDismissed = true;
    if (enterCue) enterCue.hidden = true;
    try { window.localStorage?.setItem(cueKey, '1'); } catch (_) { /* The current visit still hides the cue. */ }
  }
  enterCue?.addEventListener('click', () => {
    dismissCue();
    productStop?.scrollIntoView({behavior: reduced ? 'instant' : 'smooth', block: 'start'});
  });
  function onScroll() {
    const progress = journeyProgress();
    const browsing = progress > .025;
    if (browsing && !cueDismissed) dismissCue();
    if (enterCue) enterCue.hidden = cueDismissed || browsing;
    /* A normal upward page scroll also closes the enlarged product. */
    if (focused && (!browsing || (lastJourneyProgress !== null && progress > lastJourneyProgress + .015))) showAllThree();
    lastJourneyProgress = progress;
    viewport.classList.toggle('has-products', browsing);
    if (focused) return;
    setStage(browsing ? Math.max(1, Math.round(progress * 3)) : 0);
    sceneController?.setProgress(browsing ? Math.max(1, progress * 3) : 0);
  }

  prevButton.addEventListener('click', event => { event.preventDefault(); moveCarousel(-1); });
  nextButton.addEventListener('click', event => { event.preventDefault(); moveCarousel(1); });
  fallbackLooks.forEach((button, index) => button.addEventListener('click', () => handleLookClick(index)));
  $('[data-wardrobe-catalog-open]', viewport).addEventListener('click', openCatalog);
  $('[data-wardrobe-catalog-close]', viewport).addEventListener('click', closePanels);
  $('[data-wardrobe-inspector-close]', viewport).addEventListener('click', closePanels);
  $('[data-wardrobe-focus-back]', focusPreview).addEventListener('click', () => {
    showAllThree();
    onScroll();
  });
  $('[data-wardrobe-rotate]', viewport).addEventListener('click', () => sceneController?.nudge(selectedSlot));
  $('[data-wardrobe-photo-prev]', viewport).addEventListener('click', () => showPhoto(photoIndex - 1));
  $('[data-wardrobe-photo-next]', viewport).addEventListener('click', () => showPhoto(photoIndex + 1));

  $('[data-wardrobe-quick-form]', shop).addEventListener('submit', event => {
    event.preventDefault();
    buySelectedProduct();
  });

  $$('[data-wardrobe-catalog-product]', catalogPanel).forEach(button => button.addEventListener('click', () => {
    const index = catalogItems.findIndex(item => item?.handle === button.dataset.wardrobeCatalogProduct);
    if (index < 0) return;
    closePanels();
    goToCatalogIndex(index, true);
  }));

  viewport.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      if (!inspector.hidden || !catalogPanel.hidden) closePanels();
      else if (focused) {
        showAllThree();
        onScroll();
      }
      return;
    }
    if (event.target.closest('button, a, select, input, summary')) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      moveCarousel(event.key === 'ArrowRight' ? 1 : -1);
    }
  });

  function touchDistance(touches) {
    if (!touches || touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  }

  viewport.addEventListener('touchstart', event => {
    if (!viewport.classList.contains('has-products')) return;
    if (event.touches.length >= 2) {
      wasPinching = true;
      pinchStartDistance = touchDistance(event.touches);
      pinchStartZoom = pinchZoom;
      gestureStartY = null;
      gestureStartX = null;
      highlightCenter();
      setImageZoomOrigin((event.touches[0].clientX + event.touches[1].clientX) / 2,
        (event.touches[0].clientY + event.touches[1].clientY) / 2);
      if (event.cancelable) event.preventDefault();
      return;
    }
    wasPinching = false;
    if (event.target.closest('[data-wardrobe-shop], [data-wardrobe-catalog], [data-wardrobe-inspector], .wardrobe-bottom')) return;
    gestureStartY = event.touches[0]?.clientY ?? null;
    gestureStartX = event.touches[0]?.clientX ?? null;
  }, {passive: false});

  viewport.addEventListener('touchmove', event => {
    if (event.touches.length >= 2 && pinchStartDistance) {
      if (event.cancelable) event.preventDefault();
      const distance = touchDistance(event.touches);
      if (!distance) return;
      setImageZoom(pinchStartZoom * (distance / pinchStartDistance));
      return;
    }
    if (gestureStartY !== null && event.touches.length === 1) {
      const dx = event.touches[0].clientX - gestureStartX;
      const dy = event.touches[0].clientY - gestureStartY;
      /* Let an ordinary vertical swipe move the page; the first swipe on an enlarged look closes it. */
      if (event.cancelable && focused && dy < -12 && Math.abs(dy) > Math.abs(dx) * 1.2) event.preventDefault();
    }
  }, {passive: false});

  viewport.addEventListener('touchend', event => {
    if (wasPinching || pinchStartDistance !== null) {
      if (event.touches.length < 2) {
        pinchStartDistance = null;
        pinchStartZoom = pinchZoom;
        wasPinching = false;
      }
      gestureStartY = null;
      gestureStartX = null;
      return;
    }
    if (gestureStartY === null) return;
    const endTouch = event.changedTouches[0];
    const endY = endTouch?.clientY ?? gestureStartY;
    const endX = endTouch?.clientX ?? gestureStartX ?? 0;
    const deltaY = endY - gestureStartY;
    const deltaX = endX - (gestureStartX ?? endX);
    gestureStartY = null;
    gestureStartX = null;
    if (Math.abs(deltaX) > 55 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      suppressLookClickUntil = Date.now() + 750;
      moveCarousel(deltaX < 0 ? 1 : -1);
    } else if (deltaY < -55 && Math.abs(deltaY) > Math.abs(deltaX) * 1.2 && focused) {
      showAllThree();
      onScroll();
    }
  }, {passive: true});

  /* Trackpad pinch uses ctrl+wheel in Chromium; Safari also dispatches gesturechange. */
  viewport.addEventListener('wheel', event => {
    if (!event.ctrlKey || !sceneController || !viewport.classList.contains('has-products')) return;
    event.preventDefault();
    if (!focused) highlightCenter();
    setImageZoomOrigin(event.clientX, event.clientY);
    setImageZoom(pinchZoom * Math.exp(-event.deltaY * .012));
  }, {passive: false});
  let safariGestureStartZoom = 1;
  viewport.addEventListener('gesturestart', event => {
    if (!sceneController || !viewport.classList.contains('has-products')) return;
    event.preventDefault();
    safariGestureStartZoom = pinchZoom;
    highlightCenter();
    setImageZoomOrigin(event.clientX, event.clientY);
  }, {passive: false});
  viewport.addEventListener('gesturechange', event => {
    if (!sceneController || !viewport.classList.contains('has-products')) return;
    event.preventDefault();
    setImageZoom(safariGestureStartZoom * event.scale);
  }, {passive: false});
  viewport.addEventListener('gestureend', event => {
    if (sceneController) event.preventDefault();
  }, {passive: false});

  window.addEventListener('scroll', onScroll, {passive: true});
  window.addEventListener('resize', onScroll, {passive: true});
  window.addEventListener('resize', updateFallbackLooks, {passive: true});

  updateGroupUI();
  updateFocusUI();
  renderQuickProduct(activeProduct());
  onScroll();

  const instance = {
    dispose() {
      disposed = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('resize', updateFallbackLooks);
      document.documentElement.classList.remove('wardrobe-snap-enabled');
      sceneController?.dispose();
      instances.delete(root);
    }
  };
  instances.set(root, instance);

  if (!window.WebGLRenderingContext || !root.dataset.threeUrl) return;

  import(root.dataset.threeUrl).then(THREE => {
    if (disposed) return;
    sceneController = createRoom(THREE, canvasHost, viewport, activeGroup(), reduced, {
      onLookClick(slot) { handleLookClick(slot); },
      onSwipe(direction) { moveCarousel(direction); },
      isPinching() { return wasPinching || pinchStartDistance !== null; },
      onBackgroundClick() {
        if (!focused) return;
        showAllThree();
        onScroll();
      }
    });
    if (!sceneController) return;
    sceneController.setProgress(viewport.classList.contains('has-products') ? Math.max(1, journeyProgress() * 3) : 0);
  }).catch(() => {
    /* The quick-buy rail, catalog and product details remain fully usable without WebGL. */
  });
}

function createRoom(T, host, viewport, initialItems, reduced, callbacks = {}) {
  let renderer;
  try {
    renderer = new T.WebGLRenderer({antialias: true, alpha: true, powerPreference: 'high-performance'});
  } catch (_) { return null; }
  host.append(renderer.domElement);
  renderer.outputColorSpace = T.SRGBColorSpace;
  renderer.toneMapping = T.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.37;
  renderer.setClearColor(0xffffff, 0);
  renderer.setPixelRatio(Math.min(devicePixelRatio || 1, innerWidth < 761 ? 1.5 : 1.75));
  renderer.domElement.style.touchAction = 'pan-y';
  const scene = new T.Scene();
  scene.background = null;
  const camera = new T.OrthographicCamera(-1, 1, 1, -1, .1, 4000);
  const cameraAim = new T.Vector3(0, 0, 0);
  const cameraTarget = new T.Vector3(0, 0, 0);
  const positionTarget = new T.Vector3(0, 0, 2000);
  camera.position.copy(positionTarget); camera.lookAt(cameraAim);
  scene.add(new T.HemisphereLight(0xffffff, 0xc4a887, 3.3));
  const sun = new T.DirectionalLight(0xfff1d7, 3.4); sun.position.set(-3.8, 9, 5); scene.add(sun);
  const fill = new T.PointLight(0xffffff, 65, 23); fill.position.set(3, 5.5, -2); scene.add(fill);
  const cream = new T.MeshStandardMaterial({color: '#f1e8d9', roughness: 0.92, side: T.DoubleSide});
  const plaster = new T.MeshStandardMaterial({color: '#efe4d3', roughness: 0.96});
  const brass = new T.MeshStandardMaterial({color: '#a37a38', metalness: 0.72, roughness: 0.28});
  const walnut = new T.MeshStandardMaterial({color: '#805837', roughness: 0.53});
  const ivory = new T.MeshStandardMaterial({color: '#faf5e8', roughness: 0.84, side: T.DoubleSide});
  const soft = new T.MeshStandardMaterial({color: '#d8cab3', roughness: 0.93});
  /* The matching opening image is the visible room; the procedural fixtures are dormant. */
  const environment = new T.Group();
  environment.visible = false;
  scene.add(environment);
  const add = (geo, mat, x, y, z, group = environment) => {const mesh = new T.Mesh(geo, mat); mesh.position.set(x,y,z); group.add(mesh); return mesh;};
  const tube = (points, radius, material, group = environment, closed = false) => {
    const curve = new T.CatmullRomCurve3(points.map(p => new T.Vector3(...p)), closed);
    const mesh = new T.Mesh(new T.TubeGeometry(curve, 90, radius, 6, closed), material);
    group.add(mesh); return mesh;
  };

  function woodTexture() {
    const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext('2d'); ctx.fillStyle = '#aa855d'; ctx.fillRect(0,0,512,512);
    for (let row = 0; row < 8; row++) {
      const y = row * 64; ctx.fillStyle = row % 2 ? '#ae8b66' : '#bc9870'; ctx.fillRect(0,y,512,62);
      ctx.fillStyle = '#664a321f'; ctx.fillRect(0,y + 61,512,3);
      for (let line = 0; line < 17; line++) {
        const offset = ((line * 71 + row * 91) % 512);
        ctx.strokeStyle = line % 3 ? '#ffffff12' : '#664c3822'; ctx.lineWidth = 1 + (line % 2);
        ctx.beginPath(); ctx.moveTo(0,y + (offset % 60));
        ctx.bezierCurveTo(170,y + (offset % 60) + 4,340,y + (offset % 60) - 3,512,y + (offset % 60) + 2);ctx.stroke();
      }
    }
    const texture = new T.CanvasTexture(canvas); texture.colorSpace = T.SRGBColorSpace;
    texture.wrapS = texture.wrapT = T.RepeatWrapping; texture.repeat.set(5, 9);
    texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 8);
    return texture;
  }
  const floor = add(new T.PlaneGeometry(24, 39), new T.MeshStandardMaterial({map: woodTexture(), roughness: .62, metalness: .06}), 0, -.015, -2);
  floor.rotation.x = -Math.PI / 2;
  const ceiling = add(new T.PlaneGeometry(24, 39), plaster, 0, 7.6, -2); ceiling.rotation.x = Math.PI / 2;
  add(new T.BoxGeometry(.32, 7.8, 38), plaster, -9, 3.8, -2);
  add(new T.BoxGeometry(.32, 7.8, 38), plaster, 9, 3.8, -2);
  add(new T.BoxGeometry(18.4, 7.8, .4), plaster, 0, 3.8, -21);
  for (const x of [-8.7, 8.7]) {
    for (const z of [-12, -3, 6]) {
      const column = add(new T.CylinderGeometry(.72,.72,7.5,24), cream, x,3.75,z);
      column.scale.z = .81;
      add(new T.CylinderGeometry(.75,.75,.035,24), brass,x, .15,z);
      add(new T.CylinderGeometry(.75,.75,.035,24), brass,x,7.05,z);
    }
  }
  const floorTrack = [[0,.04,12],[.4,.04,7],[1.9,.04,3],[-.6,.04,-.8],[-2.5,.04,-4.9],[0,.04,-8],[4.3,.04,-10]];
  tube(floorTrack, .035, brass);
  tube([[-.85,.045,9],[1,.045,5],[3,.045,1],[2,.045,-2],[-1,.045,-4]], .012, new T.MeshBasicMaterial({color:'#ddba70'}));

  // The same roof opening and silk remain in place while the camera moves.
  const oculus = add(new T.CircleGeometry(3.1, 64), new T.MeshBasicMaterial({color:'#c9e2f0',side:T.DoubleSide}), 0,7.565,-3.6);
  oculus.rotation.x = Math.PI / 2; oculus.scale.y = 1.43;
  const ellipse = Array.from({length:64}, (_,i) => {
    const a = i / 64 * Math.PI * 2; return [Math.cos(a)*3.12,7.49,-3.6 + Math.sin(a)*4.45];
  });
  tube(ellipse,.10,brass,environment,true);
  for (let strip = 0; strip < 4; strip++) {
    const geo = new T.BufferGeometry(), verts = [], uvs = [], idx = [];
    const width = 1.12 + strip * .17;
    for (let i = 0; i <= 42; i++) {
      const t = i / 42, z = 8 - t * 22;
      const mid = Math.sin(t * Math.PI * 2 + strip * .88) * (1.1 + strip * .3);
      const drop = Math.sin(t * Math.PI * 4 + strip * 1.23) * .47;
      for (let edge = 0; edge < 2; edge++) {
        verts.push(mid + (edge - .5)*width, 6.4 + drop + Math.cos(edge * 3 + t * 10)*.13, z);
        uvs.push(edge,t);
      }
      if (i < 42) {const n=i*2;idx.push(n,n+2,n+1,n+1,n+2,n+3);}
    }
    geo.setAttribute('position',new T.Float32BufferAttribute(verts,3));geo.setAttribute('uv',new T.Float32BufferAttribute(uvs,2));geo.setIndex(idx);geo.computeVertexNormals();
    const fabric = new T.MeshPhysicalMaterial({color:'#fff8e9',side:T.DoubleSide,roughness:.76,transparent:true,opacity:.79,depthWrite:false});
    environment.add(new T.Mesh(geo,fabric));
  }

  // Bronze-framed mirror at the rear of this single room.
  const mirror = add(new T.PlaneGeometry(3.6,5.7), new T.MeshPhysicalMaterial({color:'#d9ceb9',metalness:.68,roughness:.22,side:T.DoubleSide}), -4.8,3.2,-20.73);
  const arch = Array.from({length:37},(_,i)=>{const a=Math.PI*i/36;return [-4.8+1.86*Math.cos(a),3.2+2.94*Math.sin(a),-20.6];});
  tube([[-6.66,.27,-20.6],[-6.66,3.2,-20.6],...arch,[-2.94,3.2,-20.6],[-2.94,.27,-20.6]], .065, brass);
  add(new T.PlaneGeometry(3.35,5.4), new T.MeshBasicMaterial({color:'#ffffff',transparent:true,opacity:.12,side:T.DoubleSide}),-4.8,3.2,-20.55);
  mirror.rotation.y = 0;

  function rail(x,z,rotation) {
    const group = new T.Group(); group.position.set(x,0,z);group.rotation.y=rotation;environment.add(group);
    for (const side of [-1,1]) {
      add(new T.CylinderGeometry(.026,.026,2.8,8),brass,side*1.9,1.5,0,group);
      add(new T.CylinderGeometry(.12,.12,.035,12),brass,side*1.9,.08,0,group);
    }
    const bar = add(new T.CylinderGeometry(.035,.035,3.86,8),brass,0,2.93,0,group); bar.rotation.z=Math.PI/2;
    for (let i=0;i<11;i++) {
      const x0=-1.62+i*.325;
      add(new T.TorusGeometry(.055,.012,5,11,Math.PI),brass,x0,2.84,0,group);
      const hanger = new T.Mesh(new T.ConeGeometry(.32,.24,3),new T.MeshStandardMaterial({color:'#ad8559',roughness:.7}));
      hanger.position.set(x0,2.69,.01);hanger.rotation.z=Math.PI;group.add(hanger);
      const tone=['#ede2c8','#9cb9d8','#d6bc8b','#a5aa87','#eee0d7'][i%5];
      const garment = add(new T.PlaneGeometry(.43,1.22),new T.MeshStandardMaterial({color:tone,side:T.DoubleSide,roughness:.9}),x0,1.92,.02,group);
      garment.rotation.y=.12*(i%3-1);
    }
  }
  rail(6.5,-7,-.14);rail(-6.4,1,.18);rail(6.4,8,-.1);


  /* Each product stands on the matching podium in the opening showroom image. */
  const homePositions = [new T.Vector3(), new T.Vector3(), new T.Vector3()];
  const homeScales = [1, 1, 1];
  function updateHomeLayout() {
    const width = Math.max(1, host.clientWidth);
    const height = Math.max(1, host.clientHeight);
    showroomLayout(width, height).forEach((point, index) => {
      homePositions[index].set(point.x - width / 2, height / 2 - point.y, 0);
      homeScales[index] = point.height / (4.17 * (index === 1 ? 1.11 : .94));
    });
  }
  updateHomeLayout();
  const textureLoader = new T.TextureLoader();
  textureLoader.setCrossOrigin('anonymous');

  /* Trim transparent margins then leave a uniform clearance around every full-body mannequin. */
  function fittedMannequinTexture(sourceTexture) {
    const image = sourceTexture?.image;
    if (!image) return sourceTexture;
    try {
      const width = Number(image.naturalWidth || image.width || 0);
      const height = Number(image.naturalHeight || image.height || 0);
      if (!width || !height) return sourceTexture;
      const scan = document.createElement('canvas');
      scan.width = 384;
      scan.height = Math.round(384 * height / width);
      const scanContext = scan.getContext('2d', {willReadFrequently: true});
      scanContext.drawImage(image, 0, 0, scan.width, scan.height);
      const pixels = scanContext.getImageData(0, 0, scan.width, scan.height).data;
      let minX = scan.width, minY = scan.height, maxX = -1, maxY = -1;
      for (let y = 0; y < scan.height; y += 2) {
        for (let x = 0; x < scan.width; x += 2) {
          if (pixels[(y * scan.width + x) * 4 + 3] < 24) continue;
          minX = Math.min(minX, x); maxX = Math.max(maxX, x);
          minY = Math.min(minY, y); maxY = Math.max(maxY, y);
        }
      }
      if (maxX < minX || maxY < minY) return sourceTexture;
      const crop = {
        x: Math.max(0, minX - 2) * width / scan.width,
        y: Math.max(0, minY - 2) * height / scan.height,
        width: Math.min(scan.width, maxX + 4) * width / scan.width,
        height: Math.min(scan.height, maxY + 4) * height / scan.height
      };
      crop.width -= crop.x;
      crop.height -= crop.y;
      const canvas = document.createElement('canvas');
      canvas.width = 512; canvas.height = 768;
      const context = canvas.getContext('2d', {alpha:true});
      const scale = Math.min(492 / crop.width, 748 / crop.height);
      const drawWidth = crop.width * scale;
      const drawHeight = crop.height * scale;
      context.drawImage(image, crop.x, crop.y, crop.width, crop.height,
        (512 - drawWidth) / 2, 768 - drawHeight - 10, drawWidth, drawHeight);
      const result = new T.CanvasTexture(canvas);
      result.colorSpace = T.SRGBColorSpace;
      result.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 6);
      sourceTexture.dispose();
      return result;
    } catch (_) {
      return sourceTexture;
    }
  }

  function normalizedCatalogTexture(sourceTexture) {
    const image = sourceTexture?.image;
    if (!image) return sourceTexture;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 768;
      const ctx = canvas.getContext('2d', {alpha: true});
      if (!ctx) return sourceTexture;
      const iw = Number(image.naturalWidth || image.videoWidth || image.width || 1);
      const ih = Number(image.naturalHeight || image.videoHeight || image.height || 1);
      const inset = 12;
      const fit = Math.min((canvas.width - inset * 2) / iw, (canvas.height - inset * 2) / ih);
      const dw = iw * fit;
      const dh = ih * fit;
      const dx = (canvas.width - dw) * .5;
      const dy = (canvas.height - dh) * .5;
      ctx.drawImage(image, dx, dy, dw, dh);

      const w = canvas.width, h = canvas.height;
      const x0 = Math.max(0, Math.floor(dx) + 2);
      const y0 = Math.max(0, Math.floor(dy) + 2);
      const x1 = Math.min(w - 1, Math.ceil(dx + dw) - 3);
      const y1 = Math.min(h - 1, Math.ceil(dy + dh) - 3);
      const pixels = ctx.getImageData(0, 0, w, h);
      const data = pixels.data;
      const corners = [[x0,y0],[x1,y0],[x0,y1],[x1,y1]].map(([x,y]) => {
        const offset = (y * w + x) * 4;
        return [data[offset],data[offset+1],data[offset+2],data[offset+3]];
      });
      if (corners.some(color => color[3] < 24)) return sourceTexture;
      {
        const bg = [0,1,2].map(channel => corners.reduce((sum,color) => sum + color[channel],0) / 4);
        const variance = Math.max(...corners.map(color => Math.hypot(...bg.map((value,channel) => value - color[channel]))));
        const light = (bg[0] + bg[1] + bg[2]) / 3 > 155;
        if (light && variance < 43) {
          /* Remove a connected, nearly uniform studio background without recolouring the garment. */
          const visited = new Uint8Array(w * h);
          const queue = new Int32Array(w * h);
          let head = 0, tail = 0;
          const enqueue = index => {
            if (visited[index]) return;
            visited[index] = 1;
            const offset = index * 4;
            if (data[offset+3] < 12) return;
            const difference = Math.hypot(data[offset]-bg[0],data[offset+1]-bg[1],data[offset+2]-bg[2]);
            if (difference < 37) queue[tail++] = index;
          };
          for (let x=x0;x<=x1;x++) {enqueue(y0*w+x);enqueue(y1*w+x);}
          for (let y=y0;y<=y1;y++) {enqueue(y*w+x0);enqueue(y*w+x1);}
          while (head < tail) {
            const index = queue[head++];
            data[index*4+3] = 0;
            const x = index % w, y = (index / w) | 0;
            if (x > x0) enqueue(index-1);
            if (x < x1) enqueue(index+1);
            if (y > y0) enqueue(index-w);
            if (y < y1) enqueue(index+w);
          }
          ctx.putImageData(pixels, 0, 0);
        }
      }

      /* Photographs with complex backgrounds remain genuine product images with softened edges. */
      ctx.globalCompositeOperation = 'destination-in';
      let mask = ctx.createLinearGradient(dx, 0, dx + dw, 0);
      mask.addColorStop(0, 'rgba(255,255,255,0)');
      mask.addColorStop(.055, 'rgba(255,255,255,1)');
      mask.addColorStop(.945, 'rgba(255,255,255,1)');
      mask.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = mask;ctx.fillRect(0, 0, w, h);
      mask = ctx.createLinearGradient(0, dy, 0, dy + dh);
      mask.addColorStop(0, 'rgba(255,255,255,0)');
      mask.addColorStop(.035, 'rgba(255,255,255,1)');
      mask.addColorStop(.965, 'rgba(255,255,255,1)');
      mask.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = mask;ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'source-over';

      const texture = new T.CanvasTexture(canvas);
      texture.colorSpace = T.SRGBColorSpace;
      texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 6);
      sourceTexture.dispose();
      return texture;
    } catch (_) {
      return sourceTexture;
    }
  }

  function curvedPortraitGeometry(width, height) {
    const geometry = new T.PlaneGeometry(width, height, 28, 40);
    const position = geometry.attributes.position;
    for (let i = 0; i < position.count; i++) {
      const x = position.getX(i);
      const y = position.getY(i);
      const nx = x / (width * .5);
      const ny = (y + height * .5) / height;
      const horizontal = Math.max(0, 1 - nx * nx);
      const torso = Math.sin(clamp(ny, 0, 1) * Math.PI);
      const depth = .045 + horizontal * (.07 + torso * .055);
      position.setZ(i, depth);
    }
    position.needsUpdate = true;
    geometry.computeVertexNormals();
    return geometry;
  }

  function makeLook(index) {
    const home = homePositions[index].clone();
    const base = new T.Group();
    base.position.copy(home);
    scene.add(base);

    const radius = index === 1 ? 1.65 : 1.32;
    const bottom = new T.Mesh(new T.CylinderGeometry(radius, radius, .28, 48), walnut);
    bottom.position.y = .16;
    bottom.userData.lookIndex = index;
    bottom.visible = false;
    base.add(bottom);

    const rim = new T.Mesh(new T.CylinderGeometry(radius + .01, radius + .01, .04, 48), brass);
    rim.position.y = .085;
    rim.visible = false;
    base.add(rim);

    const top = new T.Mesh(new T.CylinderGeometry(radius, radius, .025, 48), walnut);
    top.position.y = .32;
    top.visible = false;
    base.add(top);

    const portraitGroup = new T.Group();
    portraitGroup.position.y = 0;
    base.add(portraitGroup);

    const geometry = curvedPortraitGeometry(2.82, 4.17);
    const material = new T.MeshBasicMaterial({
      transparent: true,
      alphaTest: .025,
      depthWrite: true,
      side: T.DoubleSide,
      toneMapped: false,
      opacity: 0
    });
    const relief = new T.Mesh(geometry, material);
    relief.position.set(0, 2.1, .14);
    relief.userData.lookIndex = index;
    relief.visible = false;
    portraitGroup.add(relief);

    const holder = {
      index,
      home,
      homeScale: homeScales[index],
      displayScale: homeScales[index] * (index === 1 ? 1.11 : .94),
      base,
      bottom,
      portraitGroup,
      relief,
      material,
      item: null,
      loadToken: 0,
      loading: false,
      angleOffset: 0,
      visualOpacity: 1
    };

    holder.setProduct = async item => {
      holder.item = item || null;
      const source = item?.cutout || item?.image || null;
      const token = ++holder.loadToken;
      holder.loading = true;
      holder.relief.visible = false;
      holder.material.opacity = 0;
      if (!source) {
        holder.loading = false;
        if (holder.material.map) {
          holder.material.map.dispose();
          holder.material.map = null;
          holder.material.needsUpdate = true;
        }
        return;
      }
      try {
        let texture = await textureLoader.loadAsync(source);
        if (token !== holder.loadToken || !base.parent) {
          texture.dispose();
          return;
        }
        texture.colorSpace = T.SRGBColorSpace;
        texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 6);
        const usesRealCutout = Boolean(item?.cutout);
        texture = usesRealCutout ? fittedMannequinTexture(texture) : normalizedCatalogTexture(texture);
        holder.relief.scale.x = usesRealCutout ? .985 : .94;
        holder.relief.scale.y = usesRealCutout ? 1 : .96;
        const previous = holder.material.map;
        holder.material.map = texture;
        holder.material.needsUpdate = true;
        if (previous) previous.dispose();
        holder.relief.visible = true;
      } catch (_) {
        if (token === holder.loadToken) holder.relief.visible = false;
      } finally {
        if (token === holder.loadToken) holder.loading = false;
      }
    };

    return holder;
  }

  const holders = [makeLook(0), makeLook(1), makeLook(2)];

  const shadowCanvas = document.createElement('canvas');
  shadowCanvas.width = 128;
  shadowCanvas.height = 128;
  const shadowContext = shadowCanvas.getContext('2d');
  const shadowGradient = shadowContext.createRadialGradient(64, 64, 5, 64, 64, 64);
  shadowGradient.addColorStop(0, '#29190c70');
  shadowGradient.addColorStop(1, '#29190c00');
  shadowContext.fillStyle = shadowGradient;
  shadowContext.fillRect(0, 0, 128, 128);
  const shadowMap = new T.CanvasTexture(shadowCanvas);
  holders.forEach((holder, index) => {
    const shade = new T.Mesh(
      new T.PlaneGeometry(index === 1 ? 5 : 4, index === 1 ? 5 : 4),
      new T.MeshBasicMaterial({map: shadowMap, transparent: true, depthWrite: false, side: T.DoubleSide})
    );
    shade.rotation.x = -Math.PI / 2;
    shade.position.y = .025;
    shade.visible = false;
    holder.base.add(shade);
  });

  for (const [x, z] of [[-7, -10], [7, -12], [-7, 4]]) {
    add(new T.CylinderGeometry(.43, .47, .8, 16), ivory, x, .42, z);
    const plant = new T.Group();
    plant.position.set(x, .8, z);
    environment.add(plant);
    for (let i = 0; i < 7; i++) {
      const a = i * 2.3999;
      const dx = Math.cos(a) * .62;
      const dz = Math.sin(a) * .62;
      tube([[0,0,0],[dx*.3,.7,dz*.3],[dx,1.55,dz]], .019, new T.MeshStandardMaterial({color:'#625b3b'}), plant);
      const leaf = add(new T.SphereGeometry(.12, 6, 5), new T.MeshStandardMaterial({color:i%2?'#777e55':'#57694a'}), dx, 1.45, dz, plant);
      leaf.scale.set(.55, 1.8, .35);
      leaf.rotation.z = -a * .23;
    }
  }

  let stage = 0;
  let chosen = 1;
  let visible = true;
  let frame = 0;
  let lastTime = 0;
  let stopped = false;
  let focusTarget = 0;
  let focusAmount = 0;
  let userZoomTarget = 1;
  let userZoom = 1;
  let transition = null;
  let currentItems = [null, null, null];
  let pointer = null;
  const zoomAnchor = new T.Vector3(0, 2.4, 0);

  const raycaster = new T.Raycaster();
  const pointerNdc = new T.Vector2();

  function smooth01(value) {
    const t = clamp(value, 0, 1);
    return t * t * (3 - 2 * t);
  }
  const laneScale = index => homeScales[index] * (index === 1 ? 1.11 : .94);

  function offscreenPosition(side) {
    const edge = side < 0 ? homePositions[0] : homePositions[2];
    return new T.Vector3(edge.x + side * host.clientWidth * .5, edge.y, edge.z);
  }

  function syncHolderSlots() {
    holders.forEach((holder, index) => {
      holder.index = index;
      holder.home.copy(homePositions[index]);
      holder.homeScale = homeScales[index];
      holder.displayScale = laneScale(index);
      holder.bottom.userData.lookIndex = index;
      holder.relief.userData.lookIndex = index;
      if (!transition) holder.base.position.copy(holder.home);
    });
  }

  function setGroup(items, direction = 1, immediate = false) {
    const nextItems = [...(items || [])].slice(0, 3);
    while (nextItems.length < 3) nextItems.push(null);
    focusTarget = 0;
    userZoomTarget = 1;
    chosen = 1;
    renderer.domElement.style.touchAction = 'pan-y';

    if (immediate || reduced || !currentItems.some(Boolean)) {
      transition = null;
      currentItems = nextItems;
      syncHolderSlots();
      return Promise.all(holders.map((holder, index) => {
        holder.base.position.copy(holder.home);
        holder.base.scale.setScalar(holder.homeScale * (index === 1 ? 1.11 : .94));
        holder.visualOpacity = 1;
        return holder.setProduct(nextItems[index]);
      }));
    }

    transition = {
      start: performance.now(),
      duration: 660,
      direction: direction >= 0 ? 1 : -1,
      nextItems,
      swapped: false
    };
  }

  function focus(slot, enabled = true) {
    chosen = 1;
    focusTarget = enabled ? 1 : 0;
    if (!enabled) userZoomTarget = 1;
    renderer.domElement.style.touchAction = enabled ? 'none' : 'pan-y';
  }

  function setPinchZoom(value) {
    chosen = 1;
    if (Number(value) > 1) focusTarget = 1;
    userZoomTarget = clamp(Number(value) || 1, 1, 1.8);
  }

  function setZoomAnchor(clientX, clientY) {
    const holder = holders[1];
    const bounds = renderer.domElement.getBoundingClientRect();
    if (!holder?.relief.visible || !bounds.width || !bounds.height) return;
    pointerNdc.set((clientX - bounds.left) / bounds.width * 2 - 1,
      -((clientY - bounds.top) / bounds.height * 2 - 1));
    raycaster.setFromCamera(pointerNdc, camera);
    const hit = raycaster.intersectObject(holder.relief, false)[0];
    if (hit) zoomAnchor.copy(hit.point).sub(holder.base.position).divideScalar(holder.base.scale.x || 1);
  }

  function nudge(slot) {
    const holder = holders[1];
    if (!holder) return;
    holder.angleOffset = holder.angleOffset >= 0 ? -.035 : .035;
  }

  function updateSlideTransition(time) {
    holders.forEach((holder,index) => { holder.visualOpacity = 1; holder.displayScale = laneScale(index); });
    if (!transition) {
      holders.forEach(holder => holder.base.position.copy(holder.home));
      return false;
    }

    const raw = clamp((time - transition.start) / transition.duration, 0, 1);
    const split = .48;
    const direction = transition.direction;

    if (raw < split) {
      const p = smooth01(raw / split);
      if (direction > 0) {
        holders[0].base.position.lerpVectors(homePositions[0], offscreenPosition(-1), p);
        holders[1].base.position.lerpVectors(homePositions[1], homePositions[0], p);
        holders[2].base.position.lerpVectors(homePositions[2], homePositions[1], p);
        holders[0].visualOpacity = 1 - p;
        holders[0].displayScale = laneScale(0) * (1 - .18 * p);
        holders[1].displayScale = laneScale(1) + (laneScale(0) - laneScale(1)) * p;
        holders[2].displayScale = laneScale(2) + (laneScale(1) - laneScale(2)) * p;
      } else {
        holders[2].base.position.lerpVectors(homePositions[2], offscreenPosition(1), p);
        holders[1].base.position.lerpVectors(homePositions[1], homePositions[2], p);
        holders[0].base.position.lerpVectors(homePositions[0], homePositions[1], p);
        holders[2].visualOpacity = 1 - p;
        holders[2].displayScale = laneScale(2) * (1 - .18 * p);
        holders[1].displayScale = laneScale(1) + (laneScale(2) - laneScale(1)) * p;
        holders[0].displayScale = laneScale(0) + (laneScale(1) - laneScale(0)) * p;
      }
      return true;
    }

    if (!transition.swapped) {
      transition.swapped = true;
      currentItems = transition.nextItems;
      if (direction > 0) {
        const incoming = holders.shift();
        holders.push(incoming);
        syncHolderSlots();
        incoming.base.position.copy(offscreenPosition(1));
        incoming.visualOpacity = 0;
        incoming.setProduct(currentItems[2]);
      } else {
        const incoming = holders.pop();
        holders.unshift(incoming);
        syncHolderSlots();
        incoming.base.position.copy(offscreenPosition(-1));
        incoming.visualOpacity = 0;
        incoming.setProduct(currentItems[0]);
      }
      /* The two retained holders already carry the two retained products. */
    }

    const p = smooth01((raw - split) / (1 - split));
    if (direction > 0) {
      holders[0].base.position.copy(homePositions[0]);
      holders[1].base.position.copy(homePositions[1]);
      holders[2].base.position.lerpVectors(offscreenPosition(1), homePositions[2], p);
      holders[2].visualOpacity = p;
      holders[2].displayScale = laneScale(2) * (.82 + .18 * p);
    } else {
      holders[2].base.position.copy(homePositions[2]);
      holders[1].base.position.copy(homePositions[1]);
      holders[0].base.position.lerpVectors(offscreenPosition(-1), homePositions[0], p);
      holders[0].visualOpacity = p;
      holders[0].displayScale = laneScale(0) * (.82 + .18 * p);
    }

    if (raw >= 1) {
      transition = null;
      syncHolderSlots();
    }
    return true;
  }

  function interpolatePose(progress) {
    /* Scroll stages share one still showroom view; the product parade moves inside it. */
    positionTarget.set(0, 0, 2000);
    cameraTarget.set(0, 0, 0);
  }

  function resize() {
    const width = Math.max(1, host.clientWidth);
    const height = Math.max(1, host.clientHeight);
    updateHomeLayout();
    if (transition) {
      const nextItems = transition.nextItems;
      transition = null;
      currentItems = nextItems;
      holders.forEach((holder, index) => holder.setProduct(nextItems[index]));
    }
    syncHolderSlots();
    renderer.setSize(width, height, false);
    camera.left = -width / 2;
    camera.right = width / 2;
    camera.top = height / 2;
    camera.bottom = -height / 2;
    camera.updateProjectionMatrix();
    if (reduced) draw(performance.now());
  }

  function draw(time) {
    const dt = clamp((time - lastTime) / 1000, 0, .08);
    lastTime = time;
    const focusEase = reduced ? 1 : 1 - Math.exp(-dt * 6.2);
    focusAmount += (focusTarget - focusAmount) * focusEase;
    userZoom += (userZoomTarget - userZoom) * (reduced ? 1 : 1 - Math.exp(-dt * 8));

    interpolatePose(stage);
    if (reduced) {
      camera.position.copy(positionTarget);
      cameraAim.copy(cameraTarget);
    } else {
      const eased = 1 - Math.exp(-dt * 3.3);
      camera.position.lerp(positionTarget, eased);
      cameraAim.lerp(cameraTarget, eased);
    }
    camera.lookAt(cameraAim);

    const sliding = updateSlideTransition(time);
    holders.forEach((holder, index) => {
      const isCentre = index === 1;
      if (!sliding) holder.base.position.copy(holder.home);
      const focusScale = isCentre ? 1.11 + .06 * focusAmount : .94;
      const emphasis = focusScale * (isCentre ? userZoom : 1);
      if (isCentre && !sliding && userZoom > 1) {
        /* Scale about the part touched by the customer, without moving the room or footer. */
        holder.base.position.addScaledVector(zoomAnchor, holder.homeScale * focusScale * (1 - userZoom));
      }
      holder.base.scale.setScalar(sliding ? holder.displayScale : holder.homeScale * emphasis);

      const cameraAngle = Math.atan2(camera.position.x - holder.base.position.x, camera.position.z - holder.base.position.z);
      const sway = reduced ? 0 : Math.sin(time * .00031 + index) * .014;
      /* The enlarged photograph faces the viewer upright; the small side looks keep their subtle room angle. */
      holder.portraitGroup.rotation.y = isCentre
        ? cameraAngle * focusAmount + (1 - focusAmount) * clamp(cameraAngle * .11, -.055, .055)
        : clamp(cameraAngle * .11, -.055, .055) + holder.angleOffset + sway * .35;

      if (holder.relief.visible && !holder.loading) {
        holder.material.opacity = clamp(holder.visualOpacity, 0, 1) * (isCentre ? 1 - focusAmount : 1);
      } else {
        holder.material.opacity = 0;
      }
      holder.bottom.userData.lookIndex = index;
      holder.relief.userData.lookIndex = index;
    });

    renderer.render(scene, camera);
  }

  function loop(time) {
    frame = 0;
    if (stopped || !visible) return;
    draw(time);
    if (!reduced) frame = requestAnimationFrame(loop);
  }

  const observer = new IntersectionObserver(entries => {
    visible = Boolean(entries[0]?.isIntersecting) && !document.hidden;
    if (visible && !frame) frame = requestAnimationFrame(loop);
    if (!visible) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
  }, {rootMargin:'180px 0px'});
  observer.observe(viewport);

  const onVisibility = () => {
    visible = !document.hidden;
    if (visible && !frame) frame = requestAnimationFrame(loop);
  };
  document.addEventListener('visibilitychange', onVisibility);

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(host);
  resize();

  function onPointerDown(event) {
    if (!event.isPrimary) return;
    pointer = {x:event.clientX, y:event.clientY, lastX:event.clientX, moved:false};
  }
  function onPointerMove(event) {
    if (!pointer) return;
    const dxTotal = event.clientX - pointer.x;
    const dyTotal = event.clientY - pointer.y;
    if (Math.hypot(dxTotal, dyTotal) > 9) pointer.moved = true;
    pointer.lastX = event.clientX;
  }
  function onPointerUp(event) {
    if (callbacks.isPinching?.()) { pointer = null; return; }
    if (pointer?.moved && event.pointerType === 'mouse') {
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.2) callbacks.onSwipe?.(dx < 0 ? 1 : -1);
    }
    if (pointer && !pointer.moved) {
      const bounds = renderer.domElement.getBoundingClientRect();
      pointerNdc.set(
        (event.clientX - bounds.left) / bounds.width * 2 - 1,
        -((event.clientY - bounds.top) / bounds.height * 2 - 1)
      );
      raycaster.setFromCamera(pointerNdc, camera);
      const targets = holders.flatMap(holder => [holder.relief.visible ? holder.relief : null, holder.bottom].filter(Boolean));
      const hits = raycaster.intersectObjects(targets, true);
      if (hits.length) {
        const index = hits[0].object.userData.lookIndex;
        if (Number.isInteger(index)) callbacks.onLookClick?.(index);
      } else {
        callbacks.onBackgroundClick?.();
      }
    }
    pointer = null;
  }

  renderer.domElement.addEventListener('pointerdown', onPointerDown);
  renderer.domElement.addEventListener('pointermove', onPointerMove);
  renderer.domElement.addEventListener('pointerup', onPointerUp);
  renderer.domElement.addEventListener('pointercancel', () => { pointer = null; });
  renderer.domElement.addEventListener('webglcontextlost', event => {
    event.preventDefault();
    viewport.classList.remove('is-3d-ready');
    cancelAnimationFrame(frame);
    frame = 0;
  });

  setGroup(initialItems, 1, true).then(() => {
    if (!stopped && holders.every(holder => !holder.item || holder.relief.visible)) {
      viewport.classList.add('is-3d-ready');
    }
  });
  frame = requestAnimationFrame(loop);

  return {
    setProgress(value) {
      stage = clamp(value, 0, 3);
      if (reduced) draw(performance.now());
    },
    setGroup(items, direction = 1, immediate = false) {
      setGroup(items, direction, immediate);
      if (reduced) draw(performance.now());
    },
    focus(slot, enabled = true) {
      focus(slot, enabled);
      if (reduced) draw(performance.now());
    },
    nudge(slot) {
      nudge(slot);
      if (reduced) draw(performance.now());
    },
    setPinchZoom(value) {
      setPinchZoom(value);
      if (reduced) draw(performance.now());
    },
    setZoomAnchor,
    dispose() {
      stopped = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      resizeObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('pointerup', onPointerUp);
      holders.forEach(holder => holder.material.map?.dispose());
      scene.traverse(object => {
        if (!object.isMesh) return;
        object.geometry?.dispose();
        if (Array.isArray(object.material)) object.material.forEach(material => material.dispose());
        else object.material?.dispose();
      });
      renderer.dispose();
      renderer.domElement.remove();
    }
  };
}

function initAll(scope = document) {
  scope.querySelectorAll('[data-wardrobe]').forEach(initWardrobe);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), {once:true});
else initAll();
document.addEventListener('shopify:section:load', event => initAll(event.target));
document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-wardrobe]').forEach(element => instances.get(element)?.dispose()));

export { createRoom };
